import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const INDIAN_STATES = [
  'Andhra Pradesh', 'Arunachal Pradesh', 'Assam', 'Bihar', 'Chhattisgarh',
  'Goa', 'Gujarat', 'Haryana', 'Himachal Pradesh', 'Jharkhand', 'Karnataka',
  'Kerala', 'Madhya Pradesh', 'Maharashtra', 'Manipur', 'Meghalaya', 'Mizoram',
  'Nagaland', 'Odisha', 'Punjab', 'Rajasthan', 'Sikkim', 'Tamil Nadu',
  'Telangana', 'Tripura', 'Uttar Pradesh', 'Uttarakhand', 'West Bengal',
  'Andaman and Nicobar Islands', 'Chandigarh', 'Dadra and Nagar Haveli and Daman and Diu',
  'Delhi', 'Jammu and Kashmir', 'Ladakh', 'Lakshadweep', 'Puducherry'
];

const EditInstituteModal = ({ isOpen, onClose, institute, onSave }) => {
  const [formData, setFormData] = useState({
    institute_name: '',
    description: '',
    address: '',
    city: '',
    state: '',
    country: 'India',
    postal_code: '',
    phone: '',
    website: '',
    contact_person: '',
    contact_email: '',
    established_year: '',
    accreditation: '',
    type: '',
  });
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (institute && isOpen) {
      setFormData({
        institute_name: institute?.institute_name || institute?.instituteName || '',
        description: institute?.description || '',
        address: institute?.address || institute?.location || '',
        city: institute?.city || institute?.user?.city || '',
        state: institute?.state || institute?.user?.state || '',
        country: institute?.country || 'India',
        postal_code: institute?.postal_code || '',
        phone: institute?.phone || institute?.contact_phone || '',
        website: institute?.website || '',
        contact_person: institute?.contact_person || institute?.contactPerson || '',
        contact_email: institute?.contact_email || '',
        established_year: institute?.established_year || '',
        accreditation: institute?.accreditation || '',
        type: institute?.type || '',
      });
      setErrors({});
    }
  }, [institute, isOpen]);

  if (!isOpen || !institute) return null;

  const handleChange = (e) => {
    const { name, value } = e?.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors?.[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validate = () => {
    const newErrors = {};
    if (!formData?.institute_name?.trim()) newErrors.institute_name = 'Institute name is required';
    if (formData?.contact_email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/?.test(formData?.contact_email)) {
      newErrors.contact_email = 'Enter a valid email address';
    }
    if (formData?.website && !/^https?:\/\/.+/?.test(formData?.website)) {
      newErrors.website = 'Website must start with http:// or https://';
    }
    if (formData?.established_year && (isNaN(formData?.established_year) || formData?.established_year < 1800 || formData?.established_year > new Date()?.getFullYear())) {
      newErrors.established_year = 'Enter a valid year';
    }
    return newErrors;
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    const validationErrors = validate();
    if (Object.keys(validationErrors)?.length > 0) {
      setErrors(validationErrors);
      return;
    }
    setSaving(true);
    try {
      await onSave?.(institute?.id, formData);
      onClose?.();
    } catch (error) {
      console.error('Error saving institute:', error);
      setErrors({ submit: error?.message || 'Failed to save changes. Please try again.' });
    } finally {
      setSaving(false);
    }
  };

  const InputField = ({ label, name, type = 'text', placeholder, required = false, children }) => (
    <div>
      <label className="block text-sm font-medium text-foreground mb-1">
        {label} {required && <span className="text-error">*</span>}
      </label>
      {children || (
        <input
          type={type}
          name={name}
          value={formData?.[name] || ''}
          onChange={handleChange}
          placeholder={placeholder}
          className={`w-full px-3 py-2 text-sm border rounded-lg bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 ${
            errors?.[name] ? 'border-error' : 'border-border'
          }`}
        />
      )}
      {errors?.[name] && <p className="mt-1 text-xs text-error">{errors?.[name]}</p>}
    </div>
  );

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-background/80 backdrop-blur-sm">
      <div className="bg-card border border-border rounded-lg shadow-xl w-full max-w-3xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center">
              <Icon name="Building2" size={20} color="var(--color-primary)" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-foreground">Edit Institute Profile</h2>
              <p className="text-sm text-muted-foreground">{institute?.institute_name || institute?.instituteName}</p>
            </div>
          </div>
          <Button variant="ghost" size="icon" onClick={onClose} disabled={saving}>
            <Icon name="X" size={20} />
          </Button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto">
          <div className="p-6 space-y-6">
            {/* Basic Information */}
            <div>
              <h3 className="text-sm font-semibold text-foreground mb-3 flex items-center gap-2">
                <Icon name="Info" size={16} color="var(--color-primary)" />
                Basic Information
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="md:col-span-2">
                  <InputField label="Institute Name" name="institute_name" placeholder="Enter institute name" required />
                </div>
                <InputField label="Type" name="type" placeholder="e.g. University, College, Institute">
                  <select
                    name="type"
                    value={formData?.type || ''}
                    onChange={handleChange}
                    className="w-full px-3 py-2 text-sm border border-border rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30"
                  >
                    <option value="">Select type</option>
                    <option value="University">University</option>
                    <option value="College">College</option>
                    <option value="Institute">Institute</option>
                    <option value="School">School</option>
                    <option value="Academy">Academy</option>
                    <option value="Other">Other</option>
                  </select>
                </InputField>
                <InputField label="Established Year" name="established_year" type="number" placeholder="e.g. 1985" />
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-foreground mb-1">Description</label>
                  <textarea
                    name="description"
                    value={formData?.description || ''}
                    onChange={handleChange}
                    placeholder="Brief description about the institute"
                    rows={3}
                    className="w-full px-3 py-2 text-sm border border-border rounded-lg bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 resize-none"
                  />
                </div>
                <InputField label="Accreditation" name="accreditation" placeholder="e.g. NAAC A+, NBA" />
              </div>
            </div>

            {/* Contact Information */}
            <div>
              <h3 className="text-sm font-semibold text-foreground mb-3 flex items-center gap-2">
                <Icon name="Phone" size={16} color="var(--color-secondary)" />
                Contact Information
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <InputField label="Contact Person" name="contact_person" placeholder="Name of contact person" />
                <InputField label="Phone Number" name="phone" type="tel" placeholder="+91 XXXXX XXXXX" />
                <InputField label="Contact Email" name="contact_email" type="email" placeholder="contact@institute.edu" />
                <div className="md:col-span-2">
                  <InputField label="Website" name="website" type="url" placeholder="https://www.example.edu" />
                </div>
              </div>
            </div>

            {/* Location Details */}
            <div>
              <h3 className="text-sm font-semibold text-foreground mb-3 flex items-center gap-2">
                <Icon name="MapPin" size={16} color="var(--color-accent)" />
                Location Details
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="md:col-span-2">
                  <InputField label="Address" name="address" placeholder="Full street address" />
                </div>
                <InputField label="City" name="city" placeholder="City" />
                <InputField label="State" name="state" placeholder="State">
                  <select
                    name="state"
                    value={formData?.state || ''}
                    onChange={handleChange}
                    className="w-full px-3 py-2 text-sm border border-border rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30"
                  >
                    <option value="">Select state</option>
                    {INDIAN_STATES?.map(s => (
                      <option key={s} value={s}>{s}</option>
                    ))}
                  </select>
                </InputField>
                <InputField label="Country" name="country" placeholder="Country" />
                <InputField label="Postal Code" name="postal_code" placeholder="PIN code" />
              </div>
            </div>

            {errors?.submit && (
              <div className="bg-error/10 border border-error/30 text-error rounded-lg p-3 text-sm">
                {errors?.submit}
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="flex items-center justify-end gap-3 p-6 border-t border-border bg-card">
            <Button type="button" variant="outline" onClick={onClose} disabled={saving}>
              Cancel
            </Button>
            <Button type="submit" variant="default" disabled={saving} iconName={saving ? 'Loader2' : 'Save'}>
              {saving ? 'Saving...' : 'Save Changes'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditInstituteModal;
